import { useRef, useEffect, useState, VideoHTMLAttributes } from "react";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";

interface LazyVideoProps extends Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src'> {
  src: string;
  poster?: string;
  threshold?: number;
}

// Generate a simple gradient SVG poster as data URI
const generatePosterPlaceholder = () => {
  const svg = `
    <svg width="1920" height="1080" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:rgb(20,40,60);stop-opacity:1" />
          <stop offset="100%" style="stop-color:rgb(10,20,30);stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="1920" height="1080" fill="url(#grad)" />
    </svg>
  `;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

export function LazyVideo({ 
  src, 
  poster, 
  threshold = 0.1,
  className = "",
  style,
  ...props 
}: LazyVideoProps) {
  const [reduced, setReduced] = useState(true);
  useEffect(() => {
    const query = matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches); update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoSrc, setVideoSrc] = useState<string | undefined>(undefined);
  const { elementRef, hasIntersected } = useIntersectionObserver<HTMLDivElement>({ 
    threshold,
    freezeOnceVisible: true 
  });
  
  // Use provided poster or generate placeholder
  const posterImage = poster || generatePosterPlaceholder();

  // Attach the source once the element is on screen, and follow it if the
  // caller changes it afterwards (a page that picks its clip from data that
  // arrives after the first render must not stay stuck on the first clip).
  useEffect(() => {
    if (hasIntersected && !reduced && videoSrc !== src) {
      setVideoSrc(src);
    }
  }, [hasIntersected, src, videoSrc, reduced]);

  // Once the src is attached, load the video and, for autoplay videos,
  // explicitly start playback (the autoplay attribute alone is unreliable
  // when the src is attached after mount).
  useEffect(() => {
    const video = videoRef.current;
    if (!videoSrc || !video) return;
    const tryPlay = () => {
      video.muted = true;
      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch(() => {
          // Autoplay blocked or not ready yet: poster remains visible as fallback
        });
      }
    };
    const onCanPlay = () => {
      if (video.paused) tryPlay();
    };
    if (props.autoPlay && !reduced) {
      video.addEventListener("canplay", onCanPlay);
    }
    // Changing src already loads the media; do not reset playback here.
    if (reduced) video.pause();
    if (props.autoPlay && !reduced) {
      tryPlay();
    }
    return () => {
      video.removeEventListener("canplay", onCanPlay);
    };
  }, [videoSrc, props.autoPlay, reduced]);

  return (
    <div ref={elementRef} className="relative w-full h-full" style={{ aspectRatio: '16/9', ...style }}>
      <video
        ref={videoRef}
        src={reduced ? undefined : videoSrc}
        poster={posterImage}
        preload={props.autoPlay ? "metadata" : "none"}
        className={className}
        {...props}
        autoPlay={Boolean(props.autoPlay && !reduced)}
      />
    </div>
  );
}
